import { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError } from 'axios'

const isDevelopment = import.meta.env.DEV

interface RequestLog {
  method: string
  url: string
  timestamp: number
}

interface RequestConfigWithMetadata extends InternalAxiosRequestConfig {
  metadata?: { startTime: number }
}

export function setupAuthInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  })
}

export function setupLoggingInterceptor(client: AxiosInstance): void {
  if (!isDevelopment) return

  client.interceptors.request.use((config: RequestConfigWithMetadata) => {
    const requestLog: RequestLog = {
      method: config.method?.toUpperCase() || 'GET',
      url: config.url || '',
      timestamp: Date.now(),
    }

    config.metadata = { startTime: requestLog.timestamp }

    console.warn(`[API Request] ${requestLog.method} ${requestLog.url}`)

    return config
  })

  client.interceptors.response.use(
    (response) => {
      const config = response.config as RequestConfigWithMetadata
      const duration = Date.now() - (config.metadata?.startTime ?? 0)

      console.warn(
        `[API Response] ${config.method?.toUpperCase()} ${config.url} - ${response.status} (${duration}ms)`
      )

      return response
    },
    (error: AxiosError) => {
      const config = error.config as RequestConfigWithMetadata | undefined
      const duration = config?.metadata?.startTime ? Date.now() - config.metadata.startTime : 0

      console.error(
        `[API Error] ${config?.method?.toUpperCase()} ${config?.url} - ${error.response?.status ?? 'Network Error'} (${duration}ms)`,
        error.message
      )

      return Promise.reject(error)
    }
  )
}

let pendingUnauthorized = false

function defaultUnauthorizedHandler(): void {
  localStorage.removeItem('auth_token')
  pendingUnauthorized = true
}

let onUnauthorized = defaultUnauthorizedHandler

export function setOnUnauthorized(handler: () => void): void {
  onUnauthorized = handler
  if (pendingUnauthorized) {
    pendingUnauthorized = false
    handler()
  }
}

export function resetUnauthorizedHandler(): void {
  pendingUnauthorized = false
  onUnauthorized = defaultUnauthorizedHandler
}

/**
 * Whether a 401 means the portal session is over.
 *
 * Only api-core's do: it answers 401 for an unauthenticated caller and 403 for
 * a forbidden one. The legacy services (api-search and others) answer a missing
 * section or system, and any Keycloak RS256 token, with 401, so treating theirs
 * as an expired session signs out a user whose session is fine
 * (web-portal#87). Those reject like any other error, for the page to show.
 *
 * A request with no URL to judge keeps the old behavior and expires.
 */
function endsSession(config: InternalAxiosRequestConfig | undefined): boolean {
  if (!config?.url) return true
  const { pathname } = new URL(config.url, 'https://portal.invalid')
  return pathname.startsWith('/core/')
}

export function setupErrorInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        if (endsSession(error.config)) onUnauthorized()
        return Promise.reject(error)
      }

      if (error.response?.status === 403) {
        console.warn('Access forbidden:', error.config?.url)
      }

      return Promise.reject(error)
    }
  )
}
